import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, History, Lock, ArrowRight, CheckCircle } from 'lucide-react';

// A sample's city is the name of the mock exam paper it was taken from, set as
// `city` in public/data/exam-training/{hub}/index.json. It used to be assigned by
// list position, but the practice lists and the exam papers are in different
// orders, so "Berlin" here was a different paper from "Berlin" in the mock exam.
// Samples that belong to no offered paper carry no city and show none.

const SAMPLE_CARD_STYLES = `
  .muster-card .muster-card-arrow { transition: transform 220ms ease; }
  .muster-card:hover .muster-card-arrow { transform: translateX(4px); }
  .muster-card .muster-card-number { transition: color 220ms ease; }
  .muster-card:hover .muster-card-number { color: #3A86FF; }
  .muster-card .muster-card-watermark { transition: opacity 300ms ease, transform 300ms ease; }
  .muster-card:hover .muster-card-watermark { opacity: 0.09; transform: translate(4px, -4px) scale(1.04); }
  @media (prefers-reduced-motion: reduce) {
    .muster-card .muster-card-arrow,
    .muster-card .muster-card-number,
    .muster-card .muster-card-watermark { transition: none; }
    .muster-card:hover .muster-card-arrow,
    .muster-card:hover .muster-card-watermark { transform: none; }
  }
`;

/**
 * Full-page sample/Muster chooser shown after the user picks a Teil in a
 * practice hub (reading/listening/writing). Lists every available sample;
 * the page owning this view decides what happens on selection (e.g. paywall
 * for locked samples) and what the History button opens.
 */
const SampleSelectView = ({
  partName,
  partTypeLabel,
  samples,
  formatLabel,
  isSampleLocked,
  getSessionScore,
  getSessionProgress,
  onSelectSample,
  onBack,
  onOpenHistory,
}) => {
  const { t } = useTranslation();
  const doneCount = samples.reduce((n, _s, i) => (getSessionScore?.(i) ? n + 1 : n), 0);

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-10 animate-fade-in">
      <style>{SAMPLE_CARD_STYLES}</style>
      <div className="max-w-6xl mx-auto px-4 py-5 sm:py-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)] p-4 sm:p-5 mb-5 sm:mb-6 animate-fade-in-up">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-[#3A86FF]/45 to-transparent" />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={onBack}
              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl border border-[#E1E4E8] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-lg transition-all duration-300 touch-manipulation cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35"
              aria-label={t('common.goBack', { defaultValue: 'Back' })}
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#3A86FF]">
                {t('exam.training.chooseSample', { defaultValue: 'Choose a sample' })}
              </p>
              <h1 className="font-space text-xl sm:text-2xl font-black text-gray-900 leading-tight truncate">
                {partName}
              </h1>
              {partTypeLabel && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">{partTypeLabel}</p>
              )}
            </div>
            <button
              onClick={onOpenHistory}
              className="min-h-[44px] inline-flex items-center gap-2 rounded-xl border border-[#E1E4E8] bg-white px-3.5 py-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-lg touch-manipulation cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35"
              aria-label={t('exam.training.history', { defaultValue: 'History' })}
            >
              <History className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">{t('exam.training.history', { defaultValue: 'History' })}</span>
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3A86FF]" />
            {t('exam.training.samplesCount', { count: samples.length, defaultValue: '{{count}} tests' })}
            {doneCount > 0 && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-emerald-600">{doneCount} ✓</span>
              </>
            )}
          </div>
        </div>

        {/* Sample cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {samples.map((sample, i) => {
            const locked = isSampleLocked?.(i) || false;
            const score = getSessionScore?.(i) || null;
            // In-progress = saved answers but not yet checked: number (answered count) or true (writing draft).
            const progress = !locked && !score ? (getSessionProgress?.(i) ?? null) : null;
            const num = String(i + 1).padStart(2, '0');
            const label = formatLabel ? formatLabel(sample.label) : sample.label;
            const cityName = sample.city || '';
            const pct = score && score.total > 0 ? score.correct / score.total : null;
            const scoreClasses = pct === null ? ''
              : pct === 1 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
              : pct >= 0.6 ? 'text-amber-700 bg-amber-50 border-amber-200'
              : 'text-rose-700 bg-rose-50 border-rose-200';

            return (
              <button
                key={sample.id || i}
                onClick={() => onSelectSample(i)}
                className={`muster-card group relative overflow-hidden flex flex-col min-h-[150px] sm:min-h-[180px] rounded-2xl border-2 p-4 sm:p-5 text-left transition-all duration-300 touch-manipulation cursor-pointer animate-fade-in-up focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35 active:scale-[0.98] ${
                  locked
                    ? 'border-black/10 bg-gray-50/70 hover:border-amber-300/70 hover:shadow-[0_14px_28px_rgba(217,119,6,0.1)]'
                    : progress
                    ? 'border-[#3A86FF]/35 bg-white hover:border-[#3A86FF]/60 hover:-translate-y-1 hover:shadow-[0_18px_34px_rgba(58,134,255,0.18)]'
                    : 'border-black/10 bg-white hover:border-[#3A86FF]/40 hover:-translate-y-1 hover:shadow-[0_18px_34px_rgba(58,134,255,0.14)]'
                }`}
                style={{ animationDelay: `${i * 60}ms` }}
                aria-label={`${label}${cityName ? ` · ${cityName}` : ''}${locked ? ` · ${t('exam.training.premiumOnly', { defaultValue: 'Premium' })}` : ''}`}
              >
                {/* Top accent bar on hover */}
                <div className={`absolute inset-x-0 top-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-transparent to-transparent ${locked ? 'via-amber-400/60' : 'via-[#3A86FF]/60'}`} />

                {/* Watermark numeral */}
                <span
                  aria-hidden="true"
                  className="muster-card-watermark pointer-events-none select-none absolute -right-1 -bottom-5 font-space text-[88px] sm:text-[110px] font-black leading-none text-black opacity-[0.05]"
                >
                  {num}
                </span>

                {/* Status row */}
                <div className="relative z-10 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    {t('exam.training.probeLabel', { defaultValue: 'Test' })}
                  </span>
                  {locked ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-amber-200 bg-amber-50">
                      <Lock className="w-3.5 h-3.5 text-amber-500" />
                    </span>
                  ) : score ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : progress ? (
                    <span className="inline-flex items-center rounded-full border border-[#3A86FF]/25 bg-[#3A86FF]/8 px-2 py-0.5 text-[10px] font-bold text-[#275eb4]">
                      {typeof progress === 'number'
                        ? t('exam.training.answeredCount', { count: progress, defaultValue: '{{count}} answered' })
                        : t('exam.training.draftSaved', { defaultValue: 'Draft' })}
                    </span>
                  ) : (
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3A86FF]/60" />
                  )}
                </div>

                {/* Numeral + label */}
                <div className="relative z-10 mt-1 flex-1">
                  <p className={`muster-card-number font-space text-4xl sm:text-[52px] font-black leading-none ${locked ? 'text-gray-300' : 'text-gray-900'}`}>
                    {num}
                  </p>
                  {cityName && (
                    <p className={`mt-1.5 text-xs sm:text-sm font-semibold truncate ${locked ? 'text-gray-400' : 'text-gray-600'}`}>
                      {cityName}
                    </p>
                  )}
                </div>

                {/* Footer: score / CTA / premium */}
                <div className="relative z-10 mt-3 flex items-center justify-between gap-2">
                  {locked ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                      {t('exam.training.premiumOnly', { defaultValue: 'Premium' })}
                    </span>
                  ) : score ? (
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${scoreClasses}`}>
                      {score.correct}/{score.total}
                    </span>
                  ) : progress ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide text-[#3A86FF]">
                      {t('exam.training.continueTraining', { defaultValue: 'Continue' })}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide text-gray-500 group-hover:text-[#3A86FF] transition-colors duration-200">
                      {t('exam.training.start', { defaultValue: 'Start' })}
                    </span>
                  )}
                  <ArrowRight className={`muster-card-arrow w-4 h-4 flex-shrink-0 ${locked ? 'text-amber-400' : progress ? 'text-[#3A86FF]' : 'text-gray-300 group-hover:text-[#3A86FF]'}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SampleSelectView;
